/**
 * Форма создания и редактирования заказа швейного цеха.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ActionPanel, Button, DocumentBlock, Paper, ProductSelect } from '../uikit';
import type { DocumentItem } from '../uikit';
import type { Product } from '../api/products';
import {
  createOrder,
  updateOrder,
  findOrdersByIDs,
  ORDER_STATUSES,
  ORDER_STATUS_LABELS,
} from '../api/orders';
import { ApiError, parseApiViolations, toUserMessage } from '../api/client';
import { findProductsByIDs } from '../api/products';

const DECIMAL_2_RE = /^-?(\d+([.,]\d{0,2})?)?$/;
const INTEGER_RE = /^\d*$/;

function normalizeDecimal(value: string): string {
  return value.replace(',', '.').trim();
}

function parseDecimal(value: string): number | null {
  if (!value) return null;
  const normalized = normalizeDecimal(value);
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

const FORM_FIELD_ORDER = [
  'order-title',
  'order-customer',
  'order-date',
  'order-status',
] as const;

function focusFirstInvalidField(fieldOrder: readonly string[]): void {
  const firstInvalidId = fieldOrder.find(
    (fieldId) => document.getElementById(fieldId)?.getAttribute('aria-invalid') === 'true',
  );
  if (!firstInvalidId) return;
  const el = document.getElementById(firstInvalidId) as
    | HTMLInputElement
    | HTMLSelectElement
    | null;
  el?.focus();
}

const FormField = styled.div`
  margin-bottom: ${({ theme }) => theme.space[2]}px;
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: ${({ theme }) => theme.space[1]}px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const FormInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  max-width: 400px;
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  font-family: inherit;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  background: ${({ theme }) => theme.colors.white};
  color: ${({ theme }) => theme.colors.text.primary};

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormInputShort = styled(FormInput)`
  max-width: 120px;

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const FormInputQuantity = styled(FormInput)`
  max-width: 80px;

  ${({ theme }) => theme.device.mobile} {
    max-width: none;
  }
`;

const ErrorMessage = styled.div`
  margin-top: ${({ theme }) => theme.space[0]}px;
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  color: ${({ theme }) => theme.colors.error.main};
`;

const PageTitle = styled.h2`
  margin: 0 0 24px;
  font-size: ${({ theme }) => theme.fontSizes[9]}px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const LoadingText = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ButtonRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space[1]}px;
  flex-wrap: wrap;

  ${({ theme }) => theme.device.mobile} {
    & > button {
      width: 100%;
    }
  }
`;

const ItemsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
`;

const ItemRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.highlight.gray25};
  border-radius: ${({ theme }) => theme.borderRadius}px;
`;

const ItemRowActions = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    margin-left: 0;
    justify-content: flex-start;
  }
`;

const StatusSelect = styled.select<{ $error?: boolean }>`
  padding: ${({ theme }) => theme.space[1]}px 12px;
  font-size: ${({ theme }) => theme.fontSizes[5]}px;
  border: 1px solid
    ${({ theme, $error }) => ($error ? theme.colors.error.main : theme.colors.divider)};
  border-radius: ${({ theme }) => theme.borderRadius}px;
  min-width: 180px;

  &:focus {
    outline: none;
    border-color: ${({ theme, $error }) =>
      $error ? theme.colors.error.main : theme.colors.primary.main};
  }

  ${({ theme }) => theme.device.mobile} {
    width: 100%;
    min-width: 0;
  }
`;

const ItemProductField = styled.div`
  flex: 1 1 220px;
  min-width: min(220px, 100%);
`;

interface FormItemRow {
  id: string;
  product: Product | null;
  quantity: string;
  costPrice: string;
  salePrice: string;
  quantityError?: string;
  costPriceError?: string;
  salePriceError?: string;
}

const ORDER_ITEM_VIOLATION_RE = /^items\[(\d+)\]\.([a-zA-Z]+)$/;

interface OrderApiViolationApply {
  setTitleError: (v: string | null) => void;
  setCustomerError: (v: string | null) => void;
  setDateError: (v: string | null) => void;
  setStatusError: (v: string | null) => void;
  setDocumentsError: (v: string | null) => void;
  setServerError: (v: string | null) => void;
  setItems: Dispatch<SetStateAction<FormItemRow[]>>;
}

/**
 * Раскладывает ответ 422 по полям формы заказа; непривязанные сообщения — в serverError (только тексты violations).
 */
function applyOrderApiViolations(
  details: unknown,
  itemRowCount: number,
  apply: OrderApiViolationApply,
): void {
  const violations = parseApiViolations(details);
  const unmapped: string[] = [];

  apply.setTitleError(null);
  apply.setCustomerError(null);
  apply.setDateError(null);
  apply.setStatusError(null);
  apply.setDocumentsError(null);

  const itemPatches = new Map<number, Partial<FormItemRow>>();

  for (const v of violations) {
    const { propertyPath: path, message } = v;
    if (path === 'title') {
      apply.setTitleError(message);
      continue;
    }
    if (path === 'customer') {
      apply.setCustomerError(message);
      continue;
    }
    if (path === 'date') {
      apply.setDateError(message);
      continue;
    }
    if (path === 'status') {
      apply.setStatusError(message);
      continue;
    }
    if (path === 'items') {
      unmapped.push(message);
      continue;
    }
    if (path.startsWith('documents')) {
      apply.setDocumentsError(message);
      continue;
    }
    const m = path.match(ORDER_ITEM_VIOLATION_RE);
    if (m) {
      const index = Number.parseInt(m[1], 10);
      const field = m[2];
      if (index < 0 || index >= itemRowCount) {
        unmapped.push(message);
        continue;
      }
      const prevPatch = itemPatches.get(index) ?? {};
      if (field === 'quantity') {
        itemPatches.set(index, { ...prevPatch, quantityError: message });
      } else if (field === 'costPrice') {
        itemPatches.set(index, { ...prevPatch, costPriceError: message });
      } else if (field === 'salePrice') {
        itemPatches.set(index, { ...prevPatch, salePriceError: message });
      } else {
        unmapped.push(message);
      }
      continue;
    }
    unmapped.push(message);
  }

  apply.setItems((prev) =>
    prev.map((r, i) => {
      const cleared: FormItemRow = {
        ...r,
        quantityError: undefined,
        costPriceError: undefined,
        salePriceError: undefined,
      };
      const patch = itemPatches.get(i);
      return patch ? { ...cleared, ...patch } : cleared;
    }),
  );

  apply.setServerError(unmapped.length > 0 ? unmapped.join(' ') : null);
}

function generateRowId(): string {
  return `row-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function OrderFormPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [title, setTitle] = useState('');
  const [customer, setCustomer] = useState('');
  const [date, setDate] = useState('');
  const [status, setStatus] = useState('new');
  const [items, setItems] = useState<FormItemRow[]>([]);
  const [documents, setDocuments] = useState<DocumentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [titleError, setTitleError] = useState<string | null>(null);
  const [customerError, setCustomerError] = useState<string | null>(null);
  const [dateError, setDateError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [documentsError, setDocumentsError] = useState<string | null>(null);
  const [serverError, setServerError] = useState<string | null>(null);

  const initialSnapshotRef = useRef<{
    title: string;
    customer: string;
    date: string;
    status: string;
    itemsHash: string;
    documentsHash: string;
  } | null>(null);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setFetchLoading(true);
    setLoadError(null);
    findOrdersByIDs([id])
      .then(async (res) => {
        if (!cancelled) {
          const o = res.items[id] ?? null;
          if (o) {
            setTitle(o.title);
            setCustomer(o.customer);
            setDate(o.date);
            setStatus(o.status || 'new');

            const orderItems = o.items ?? [];
            let productsMap: Record<string, Product> = {};
            if (orderItems.length > 0) {
              const productIds = [...new Set(orderItems.map((it) => it.productId))];
              const productsRes = await findProductsByIDs(productIds);
              productsMap = productsRes.items as Record<string, Product>;
            }

            setItems(
              orderItems.map((it) => ({
                id: generateRowId(),
                product: (productsMap[it.productId] ?? null) as Product | null,
                quantity: String(it.quantity),
                costPrice: it.costPrice ?? '',
                salePrice: it.salePrice ?? '',
              })),
            );
            const orderDocuments = o.documents ?? [];
            setDocuments(
              orderDocuments.map((d) => ({
                file: {
                  id: d.file.id,
                  uri: d.file.uri,
                  filename: d.file.filename,
                  size: d.file.size,
                },
              })),
            );
            initialSnapshotRef.current = {
              title: o.title,
              customer: o.customer,
              date: o.date,
              status: o.status || 'new',
              itemsHash: JSON.stringify(o.items ?? []),
              documentsHash: JSON.stringify(orderDocuments),
            };
          } else {
            setLoadError('Заказ не найден');
          }
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setLoadError(toUserMessage(err));
        }
      })
      .finally(() => {
        if (!cancelled) setFetchLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  useEffect(() => {
    if (isEdit) return;
    if (items.length === 0) {
      setItems([
        {
          id: generateRowId(),
          product: null,
          quantity: '',
          costPrice: '',
          salePrice: '',
        },
      ]);
    }
  }, [isEdit, items.length]);

  const addItemRow = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        id: generateRowId(),
        product: null,
        quantity: '',
        costPrice: '',
        salePrice: '',
      },
    ]);
  }, []);

  const removeItemRow = useCallback((rowId: string) => {
    setItems((prev) => prev.filter((r) => r.id !== rowId));
  }, []);

  const updateItemRow = useCallback((rowId: string, patch: Partial<FormItemRow>) => {
    setItems((prev) =>
      prev.map((r) => (r.id === rowId ? { ...r, ...patch } : r)),
    );
  }, []);

  const validateItems = useCallback((): boolean => {
    let allValid = true;
    const updates: Array<{ id: string; patch: Partial<FormItemRow> }> = [];

    for (const row of items) {
      const patch: Partial<FormItemRow> = {};
      const hasProduct = Boolean(row.product?.id);

      const qty = parseInt(row.quantity, 10);
      const qtyValid = row.quantity !== '' && Number.isInteger(qty) && qty > 0;
      if (hasProduct && !qtyValid) {
        patch.quantityError = 'Введите целое число > 0';
        allValid = false;
      } else {
        patch.quantityError = undefined;
      }

      const costParsed = parseDecimal(row.costPrice);
      const costValid = row.costPrice === '' || (costParsed !== null && costParsed >= 0);
      if (hasProduct && !costValid) {
        patch.costPriceError = 'Введите число, например 123.45';
        allValid = false;
      } else {
        patch.costPriceError = undefined;
      }

      const saleParsed = parseDecimal(row.salePrice);
      const saleValid = row.salePrice === '' || (saleParsed !== null && saleParsed >= 0);
      if (hasProduct && !saleValid) {
        patch.salePriceError = 'Введите число, например 123.45';
        allValid = false;
      } else {
        patch.salePriceError = undefined;
      }

      updates.push({ id: row.id, patch });
    }

    setItems((prev) =>
      prev.map((r) => {
        const u = updates.find((x) => x.id === r.id);
        return u ? { ...r, ...u.patch } : r;
      }),
    );
    return allValid;
  }, [items]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTitleError(null);
    setCustomerError(null);
    setDateError(null);
    setStatusError(null);
    setDocumentsError(null);
    setServerError(null);

    const hasTitleError = !title.trim();
    if (hasTitleError) setTitleError('Введите название заказа');

    const hasCustomerError = !customer.trim();
    if (hasCustomerError) setCustomerError('Укажите заказчика');

    const hasDateError = !date.trim();
    if (hasDateError) setDateError('Введите дату');

    const validItems = items.filter((r) => r.product?.id);
    const allItemsValid = validateItems();

    if (
      hasTitleError ||
      hasCustomerError ||
      hasDateError ||
      !allItemsValid ||
      validItems.length === 0
    ) {
      if (validItems.length === 0) {
        setServerError('Добавьте хотя бы одну позицию с изделием');
      }
      focusFirstInvalidField(FORM_FIELD_ORDER);
      return;
    }

    const apiItems = validItems.map((r) => ({
      productId: r.product!.id,
      quantity: parseInt(r.quantity, 10),
      costPrice: r.costPrice.trim() || '0',
      salePrice: r.salePrice.trim() || '0',
    }));

    const apiDocuments = documents.map((d) => ({ file: d.file }));

    setLoading(true);
    try {
      if (isEdit && id) {
        await updateOrder({
          id,
          title: title.trim(),
          customer: customer.trim(),
          date: date.trim(),
          status,
          items: apiItems,
          documents: apiDocuments,
        });
        navigate(`/orders/${id}`);
      } else {
        const created = await createOrder({
          title: title.trim(),
          customer: customer.trim(),
          date: date.trim(),
          status,
          items: apiItems,
          documents: apiDocuments,
        });
        navigate(`/orders/${created.id}`);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 422 && err.details) {
        const parsed = parseApiViolations(err.details);
        if (parsed.length > 0) {
          applyOrderApiViolations(err.details, items.length, {
            setTitleError,
            setCustomerError,
            setDateError,
            setStatusError,
            setDocumentsError,
            setServerError,
            setItems,
          });
          focusFirstInvalidField(FORM_FIELD_ORDER);
        } else {
          setServerError(toUserMessage(err));
        }
      } else {
        setServerError(toUserMessage(err));
      }
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return <LoadingText>Загрузка…</LoadingText>;
  }

  if (isEdit && loadError) {
    return (
      <>
        <ActionPanel defaultPath="/orders" />
        <Paper padding={24} shadow={2} style={{ marginTop: 16 }}>
          <PageTitle>Редактировать заказ</PageTitle>
          <ErrorMessage role="alert" aria-live="polite" style={{ marginBottom: 16 }}>
            {loadError}
          </ErrorMessage>
          <ButtonRow>
            <Button type="button" variant="filled" color="primary" onClick={() => setReloadKey((prev) => prev + 1)}>
              Повторить
            </Button>
          </ButtonRow>
        </Paper>
      </>
    );
  }

  return (
    <Paper padding={24} shadow={2}>
      <PageTitle>{isEdit ? 'Редактировать заказ' : 'Новый заказ'}</PageTitle>
      <form onSubmit={handleSubmit}>
        <FormField>
          <FormLabel htmlFor="order-title">Название *</FormLabel>
          <FormInput
            id="order-title"
            type="text"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (titleError) setTitleError(null);
            }}
            placeholder="Название заказа"
            autoComplete="off"
            aria-required
            aria-invalid={Boolean(titleError)}
            aria-describedby={titleError ? 'order-title-error' : undefined}
            $error={Boolean(titleError)}
          />
          {titleError && (
            <ErrorMessage id="order-title-error" role="alert">
              {titleError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="order-customer">Заказчик *</FormLabel>
          <FormInput
            id="order-customer"
            type="text"
            value={customer}
            onChange={(e) => {
              setCustomer(e.target.value);
              if (customerError) setCustomerError(null);
            }}
            placeholder="Имя заказчика"
            enterKeyHint="next"
            autoComplete="off"
            aria-required
            aria-invalid={Boolean(customerError)}
            aria-describedby={customerError ? 'order-customer-error' : undefined}
            $error={Boolean(customerError)}
          />
          {customerError && (
            <ErrorMessage id="order-customer-error" role="alert">
              {customerError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="order-date">Дата *</FormLabel>
          <FormInput
            id="order-date"
            type="date"
            value={date}
            onChange={(e) => {
              setDate(e.target.value);
              if (dateError) setDateError(null);
            }}
            aria-required
            aria-invalid={Boolean(dateError)}
            aria-describedby={dateError ? 'order-date-error' : undefined}
            $error={Boolean(dateError)}
          />
          {dateError && (
            <ErrorMessage id="order-date-error" role="alert">
              {dateError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel htmlFor="order-status">Статус</FormLabel>
          <StatusSelect
            id="order-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              if (statusError) setStatusError(null);
            }}
            aria-label="Статус заказа"
            aria-invalid={Boolean(statusError)}
            aria-describedby={statusError ? 'order-status-error' : undefined}
            $error={Boolean(statusError)}
          >
            {ORDER_STATUSES.map((s) => (
              <option key={s} value={s}>
                {ORDER_STATUS_LABELS[s]}
              </option>
            ))}
          </StatusSelect>
          {statusError && (
            <ErrorMessage id="order-status-error" role="alert">
              {statusError}
            </ErrorMessage>
          )}
        </FormField>

        <FormField>
          <FormLabel id="order-documents-label">Документы</FormLabel>
          <div role="group" aria-labelledby="order-documents-label">
            <DocumentBlock
              id="order-documents"
              value={documents}
              onChange={(next) => {
                setDocuments(next);
                if (documentsError) setDocumentsError(null);
              }}
              disabled={loading}
            />
          </div>
          {documentsError && (
            <ErrorMessage id="order-documents-error" role="alert">
              {documentsError}
            </ErrorMessage>
          )}
        </FormField>

        <ItemsSection>
          <h3 style={{ margin: '0 0 12px', fontSize: 16 }}>Позиции</h3>
          {items.map((row) => (
            <ItemRow key={row.id}>
              <ItemProductField>
                <FormLabel htmlFor={`item-product-${row.id}`}>Изделие</FormLabel>
                <ProductSelect
                  value={row.product}
                  onChange={(p) => updateItemRow(row.id, { product: p ?? null })}
                  placeholder="Выберите изделие"
                />
              </ItemProductField>
              <div>
                <FormLabel htmlFor={`item-qty-${row.id}`}>Кол-во, шт</FormLabel>
                <FormInputQuantity
                  id={`item-qty-${row.id}`}
                  type="text"
                  inputMode="numeric"
                  value={row.quantity}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || INTEGER_RE.test(v)) {
                      updateItemRow(row.id, { quantity: v, quantityError: undefined });
                    }
                  }}
                  placeholder="0"
                  $error={Boolean(row.quantityError)}
                  aria-invalid={Boolean(row.quantityError)}
                />
                {row.quantityError && (
                  <ErrorMessage role="alert">{row.quantityError}</ErrorMessage>
                )}
              </div>
              <div>
                <FormLabel htmlFor={`item-cost-${row.id}`}>Себестоимость, руб</FormLabel>
                <FormInputShort
                  id={`item-cost-${row.id}`}
                  type="text"
                  inputMode="decimal"
                  value={row.costPrice}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || DECIMAL_2_RE.test(v)) {
                      updateItemRow(row.id, { costPrice: v, costPriceError: undefined });
                    }
                  }}
                  placeholder="0.00"
                  $error={Boolean(row.costPriceError)}
                />
                {row.costPriceError && (
                  <ErrorMessage role="alert">{row.costPriceError}</ErrorMessage>
                )}
              </div>
              <div>
                <FormLabel htmlFor={`item-sale-${row.id}`}>Цена продажи, руб</FormLabel>
                <FormInputShort
                  id={`item-sale-${row.id}`}
                  type="text"
                  inputMode="decimal"
                  value={row.salePrice}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '' || DECIMAL_2_RE.test(v)) {
                      updateItemRow(row.id, { salePrice: v, salePriceError: undefined });
                    }
                  }}
                  placeholder="0.00"
                  $error={Boolean(row.salePriceError)}
                />
                {row.salePriceError && (
                  <ErrorMessage role="alert">{row.salePriceError}</ErrorMessage>
                )}
              </div>
              <ItemRowActions>
                <Button
                  type="button"
                  variant="outlined"
                  color="primary"
                  size="s"
                  onClick={() => removeItemRow(row.id)}
                  aria-label="Удалить позицию"
                >
                  Удалить
                </Button>
              </ItemRowActions>
            </ItemRow>
          ))}
          <div style={{ marginTop: 8 }}>
            <Button
              type="button"
              variant="outlined"
              color="primary"
              size="s"
              onClick={addItemRow}
            >
              Добавить позицию
            </Button>
          </div>
        </ItemsSection>

        {serverError && (
          <ErrorMessage role="alert" aria-live="polite" style={{ marginTop: 16 }}>
            {serverError}
          </ErrorMessage>
        )}

        <ButtonRow style={{ marginTop: 24 }}>
          <Button type="submit" variant="filled" color="primary" loading={loading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="primary"
            onClick={() => navigate(isEdit && id ? `/orders/${id}` : '/orders')}
          >
            Отмена
          </Button>
        </ButtonRow>
      </form>
    </Paper>
  );
}
